import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DOCUMENT_KIND_CHOICES,
  DOCUMENT_KIND_LABELS,
  MOVEMENT_KINDS,
  PAYMENT_METHODS,
  accountLabel,
  classifyMovement,
  findPeriodClash,
  kindCreatesExpense,
  reconcileImport,
  type DocumentKindChoice,
  type ExpenseAccount,
  type MovementKind,
  type Platform,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import {
  apiErrorMessage,
  confirmImport,
  discardImport,
  extractFile,
  fetchAccess,
  fetchImport,
  fetchImports,
  fetchPlatforms,
} from "../services/expensesService";
import {
  fetchAccounts,
  importStatement,
  updateImportMeta,
  type StatementImportPayload,
  type StatementImportRecord,
  type StatementPreviewCharge,
} from "../services/statementsService";
import { isStatementFile, readStatementFile, type StatementFile } from "../lib/statementFile";
import { Chip, ErrorBox, Field, FileDrop, Kpi, Notice, Page, Spinner } from "../components/ui";
import StatementMapper from "../components/StatementMapper";
import ReconcilePanel, { RECONCILE_TONES, parseDeclared, type ImportMeta } from "../components/ReconcilePanel";
import { categoryOptions, dateTime, money } from "../lib/format";
import { INCOME_NEW_AI_PATH } from "../lib/incomeRoutes";
import { ui } from "../theme";

// Importar gastos desde capturas, recibos y estados de cuenta (#609). La IA
// propone; nada se guarda hasta que la persona revisa y confirma.
// Estados de cuenta del banco (#952 – #954): cada importación se liga a una
// cuenta y un periodo; CSV / Excel / OFX se leen sin IA con mapeo de
// columnas; cada movimiento lleva su tipo (gasto, pago a tarjeta propia,
// traspaso, devolución, ingreso) y la importación se concilia contra el total
// que declara el banco.

const NS = "expensesStatements";

type Row = StatementPreviewCharge & { selected: boolean; forceDuplicate?: boolean; justification?: string; kindManual?: boolean };

/** Tipo efectivo: una importación anterior a #954 no trae `kind`. */
const kindOf = (r: Pick<Row, "kind" | "isCredit">): MovementKind | null => r.kind ?? (r.isCredit ? null : "gasto");
const selectable = (r: Pick<Row, "kind" | "isCredit">) => kindCreatesExpense(kindOf(r));

const toRows = (rec: StatementImportRecord): Row[] => rec.charges.map((ch) => ({ ...ch, selected: selectable(ch) && !ch.duplicateOf }));

const metaOf = (rec: StatementImportRecord): ImportMeta => ({
  accountId: rec.accountId || "",
  from: rec.statementPeriod?.from || "",
  to: rec.statementPeriod?.to || "",
  declared: rec.statementTotal != null ? String(rec.statementTotal) : "",
});

export default function ImportSection() {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  const [aiReady, setAiReady] = useState<boolean | null>(null);
  const [hint, setHint] = useState("");
  // Tipo de documento (#1274): lo elige quien sube o lo identifica la IA.
  const [docKind, setDocKind] = useState<DocumentKindChoice>("auto");
  const [busy, setBusy] = useState<"" | "reading" | "saving">("");
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<StatementImportRecord | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [defaults, setDefaults] = useState({ justification: "", paymentMethod: "tarjeta_credito", cardLast4: "" });
  const [rates, setRates] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<StatementImportRecord[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<ExpenseAccount[]>([]);
  const [uploadAccount, setUploadAccount] = useState("");
  const [pending, setPending] = useState<{ file: File; parsed: StatementFile } | null>(null);
  const [meta, setMeta] = useState<ImportMeta>({ accountId: "", from: "", to: "", declared: "" });
  const [savingMeta, setSavingMeta] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      setHistory((await fetchImports()) as StatementImportRecord[]);
    } catch {
      setHistory([]);
    }
  }, []);
  const loadAccounts = useCallback(() => {
    fetchAccounts()
      .then((a) => setAccounts(Array.isArray(a) ? a : []))
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    fetchAccess().then((a) => setAiReady(Boolean(a.aiConfigured))).catch(() => setAiReady(null));
    fetchPlatforms().then(setPlatforms).catch(() => setPlatforms([]));
    loadAccounts();
    void loadHistory();
  }, [loadHistory, loadAccounts]);

  const activeAccounts = accounts.filter((a) => a.active !== false);
  const accountById = (id?: string | null) => accounts.find((a) => a.id === id) || null;

  const applyAccountDefaults = (accountId: string, card?: string | null) => {
    const a = accountById(accountId);
    setDefaults((d) => ({
      ...d,
      paymentMethod: a ? (a.type === "credito" ? "tarjeta_credito" : "transferencia") : d.paymentMethod,
      cardLast4: card || (a && a.type === "credito" ? a.last4 || "" : d.cardLast4),
    }));
  };

  const loadRecord = (rec: StatementImportRecord) => {
    setRecord(rec);
    setRows(toRows(rec));
    setMeta(metaOf(rec));
    const card = rec.charges.find((ch) => ch.cardLast4)?.cardLast4;
    if (rec.accountId) applyAccountDefaults(rec.accountId, card);
    else if (card) setDefaults((d) => ({ ...d, cardLast4: card }));
  };

  const read = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setError(null);
    setResult(null);
    if (isStatementFile(file)) {
      if (!uploadAccount) {
        setError(tt("import.accountRequired"));
        return;
      }
      setBusy("reading");
      try {
        setPending({ file, parsed: await readStatementFile(file) });
      } catch {
        setError(tt("import.fileError"));
      } finally {
        setBusy("");
      }
      return;
    }
    if (aiReady === false) {
      setError(t("expenses.import.aiMissing"));
      return;
    }
    setBusy("reading");
    try {
      const rec = (await extractFile(file, hint || undefined, { statement: docKind === "estado_de_cuenta", accountId: uploadAccount || undefined, kind: docKind })) as StatementImportRecord;
      loadRecord(rec);
      void loadHistory();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.extract"));
    } finally {
      setBusy("");
    }
  };

  const submitStatement = async (payload: Omit<StatementImportPayload, "accountId">) => {
    if (!pending) return;
    setBusy("saving");
    setError(null);
    try {
      const rec = await importStatement(pending.file, { ...payload, accountId: uploadAccount });
      setPending(null);
      loadRecord(rec);
      loadAccounts();
      void loadHistory();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.extract"));
    } finally {
      setBusy("");
    }
  };

  const open = async (id: string) => {
    try {
      loadRecord((await fetchImport(id)) as StatementImportRecord);
      setResult(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  };

  const patch = (i: number, p: Partial<Row>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p } : r)));

  const setKind = (i: number, kind: MovementKind) =>
    setRows((rs) => rs.map((r, k) => (k === i ? { ...r, kind, kindManual: true, kindReason: null, selected: kindCreatesExpense(kind) && !r.duplicateOf } : r)));

  // Cambiar la cuenta reclasifica los movimientos que nadie tocó a mano.
  const changeMeta = (m: ImportMeta) => {
    if (m.accountId !== meta.accountId && record?.status === "pendiente") {
      const account = accountById(m.accountId);
      setRows((rs) =>
        rs.map((r) => {
          if (r.kindManual) return r;
          const k = classifyMovement(r, { account, accounts });
          return { ...r, kind: k.kind, kindReason: k.reason, relatedAccountId: k.relatedAccountId, selected: kindCreatesExpense(k.kind) && !r.duplicateOf };
        }),
      );
      applyAccountDefaults(m.accountId);
    }
    setMeta(m);
  };

  const selected = rows.filter((r) => r.selected && selectable(r));
  const totals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of selected) {
      const [cur, value] = r.billedAmount != null ? ["MXN", Number(r.billedAmount)] : [r.currency, Number(r.amount)];
      const signed = kindOf(r) === "devolucion" ? -value : value;
      out[cur] = Math.round(((out[cur] || 0) + signed) * 100) / 100;
    }
    return out;
  }, [selected]);
  const readTotals = record?.totals || {};
  // Solo se pide tipo de cambio para los cargos que no lo traen del documento.
  const foreign = [...new Set(selected.filter((r) => r.currency !== "MXN" && !(Number(r.exchangeRate) > 0)).map((r) => r.currency))];
  const missingRate = foreign.some((cur) => !(Number(rates[cur]) > 0));

  const period = meta.from && meta.to ? { from: meta.from, to: meta.to } : null;
  const clash = useMemo(() => findPeriodClash(history, meta.accountId || null, period, record?.id), [history, meta.accountId, meta.from, meta.to, record?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const reconciliation = useMemo(
    () =>
      reconcileImport({
        status: record?.status,
        currency: record?.currency || accountById(meta.accountId)?.currency || "MXN",
        statementTotal: parseDeclared(meta.declared),
        charges: rows.map((r) => ({ ...r, kind: kindOf(r) })),
      }),
    [record, rows, meta.declared, meta.accountId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const confirm = async () => {
    if (!record) return;
    setBusy("saving");
    setError(null);
    try {
      const kinds = Object.fromEntries(rows.filter((r) => kindOf(r)).map((r) => [r.index, kindOf(r) as string]));
      const r = await confirmImport(
        record.id,
        selected,
        {
          justification: defaults.justification || undefined,
          paymentMethod: defaults.paymentMethod,
          cardLast4: defaults.cardLast4 || undefined,
          exchangeRates: Object.fromEntries(foreign.map((cur) => [cur, Number(rates[cur])])),
        },
        {
          accountId: meta.accountId || null,
          statementPeriod: period,
          statementTotal: parseDeclared(meta.declared),
          kinds,
        },
      );
      setResult(t("expenses.import.done", { created: r.created, skipped: r.skipped.length }));
      setRecord(null);
      setRows([]);
      void loadHistory();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy("");
    }
  };

  const saveMeta = async () => {
    if (!record) return;
    setSavingMeta(true);
    setError(null);
    try {
      const updated = await updateImportMeta(record.id, { accountId: meta.accountId || null, statementPeriod: period, statementTotal: parseDeclared(meta.declared) });
      loadRecord(updated);
      setResult(tt("reconcile.saved"));
      void loadHistory();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setSavingMeta(false);
    }
  };

  const confirmed = record?.status === "confirmada";
  // El documento leído es un estado de cuenta (distinto de lo elegido en `docKind`, #1029).
  const recordIsStatement = record?.documentType === "estado_de_cuenta";
  // Una sola operación que resultó ser dinero que entró (#1274): no es un gasto, va a Ingresos.
  const incomeOnly = !recordIsStatement && rows.length > 0 && rows.every((r) => kindOf(r) === "ingreso" || (r.isCredit && !kindOf(r)));
  const sameDocCharges = rows.filter((r) => kindOf(r) === "gasto");

  return (
    <Page title={t("expenses.menu.import")} subtitle={t("expenses.import.subtitle")}>
      <ErrorBox message={error} />
      {result && <Notice tone="ok">{result}</Notice>}
      {aiReady === false && <Notice tone="warn">{t("expenses.import.aiMissing")}</Notice>}

      {!record && !pending && (
        <div className={`${c.card} space-y-3`}>
          <Field label={tt("import.account")}>
            <select aria-label="upload-account" className={c.input} value={uploadAccount} onChange={(e) => setUploadAccount(e.target.value)}>
              <option value="">{tt("import.accountPick")}</option>
              {activeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {accountLabel(a)}
                </option>
              ))}
            </select>
          </Field>
          <FileDrop
            accept="image/png,image/jpeg,image/webp,application/pdf,.csv,text/csv,.xlsx,.ofx,.qfx"
            disabled={Boolean(busy)}
            label={t("expenses.import.drop")}
            hint={tt("import.dropHint")}
            onFiles={read}
          />
          <Field label={t("expenses.import.hint")}>
            <input className={c.input} value={hint} onChange={(e) => setHint(e.target.value)} placeholder={t("expenses.import.hintPlaceholder")} />
          </Field>
          <Field label={tt("incomeAi.kind")}>
            <select aria-label="document-kind" className={c.input} value={docKind} onChange={(e) => setDocKind(e.target.value as DocumentKindChoice)}>
              {DOCUMENT_KIND_CHOICES.map((k) => (
                <option key={k} value={k}>
                  {DOCUMENT_KIND_LABELS[k][i18n.language.startsWith("en") ? "en" : "es"]}
                </option>
              ))}
            </select>
          </Field>
          <p className={`text-xs ${c.muted}`}>
            {docKind === "estado_de_cuenta" && <i className="ri-lock-2-line mr-1 text-teal-600" />}
            {tt(`import.kindHint.${docKind}`)}{" "}
            <a href={INCOME_NEW_AI_PATH} className="text-teal-600 hover:underline">
              {tt("import.incomeLink")}
            </a>
          </p>
          {busy === "reading" && <Spinner label={t("expenses.import.reading")} />}
        </div>
      )}

      {pending && accountById(uploadAccount) && (
        <StatementMapper
          fileName={pending.file.name}
          parsed={pending.parsed}
          account={accountById(uploadAccount) as ExpenseAccount}
          busy={busy === "saving"}
          onCancel={() => setPending(null)}
          onSubmit={submitStatement}
        />
      )}

      {record && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t("expenses.import.document")} value={t(`expenses.import.docType.${record.documentType}`)} hint={[record.issuer, record.file.name].filter(Boolean).join(" · ")} />
            <Kpi
              label={t("expenses.import.period")}
              value={record.statementPeriod?.from ? `${record.statementPeriod.from} → ${record.statementPeriod.to || "?"}` : "—"}
            />
            <Kpi
              label={t("expenses.import.readTotal")}
              value={Object.entries(readTotals).map(([cur, v]) => money(v, cur, i18n.language)).join(" + ") || "—"}
              hint={record.statementTotal != null ? t("expenses.import.statementTotal", { amount: money(record.statementTotal, record.currency || "MXN", i18n.language) }) : undefined}
            />
            <Kpi label={t("expenses.import.selectedTotal")} value={Object.entries(totals).map(([cur, v]) => money(v, cur, i18n.language)).join(" + ") || "—"} hint={t("expenses.import.selectedCount", { count: selected.length })} />
          </div>
          {/* Un comprobante de dinero que entró llega con renglones de ingreso o, si la IA solo lista gastos, vacío. */}
          {(incomeOnly || (rows.length === 0 && !recordIsStatement)) && !confirmed && (
            <Notice tone="warn">
              {tt(incomeOnly ? "import.incomeOnly" : "import.noExpenses")}{" "}
              <a href={INCOME_NEW_AI_PATH} className="font-medium underline">
                {tt("import.incomeLink")}
              </a>
            </Notice>
          )}
          {record.notes && <Notice>{record.notes}</Notice>}
          {record.file.encrypted && (
            <Notice tone="ok">
              <i className="ri-lock-2-line mr-1" />
              {t("expenses.statements.savedEncrypted")}
            </Notice>
          )}
          {confirmed && <Notice tone="ok">{t("expenses.import.alreadyConfirmed", { count: record.createdExpenseIds.length })}</Notice>}

          {(recordIsStatement || accounts.length > 0) && (
            <ReconcilePanel
              meta={meta}
              onChange={changeMeta}
              accounts={accounts}
              suggested={Boolean(record.accountSuggested) && meta.accountId === (record.accountId || "")}
              reconciliation={reconciliation}
              clash={clash}
              confirmed={confirmed}
              onSave={saveMeta}
              saving={savingMeta}
            />
          )}

          {!confirmed && (
            <div className={`${c.card} grid grid-cols-1 gap-3 sm:grid-cols-3`}>
              <Field label={t("expenses.import.defaultJustification")}>
                <input className={c.input} value={defaults.justification} onChange={(e) => setDefaults({ ...defaults, justification: e.target.value })} placeholder={t("expenses.fields.justificationHint")} />
              </Field>
              <Field label={t("expenses.fields.paymentMethod")}>
                <select className={c.input} value={defaults.paymentMethod} onChange={(e) => setDefaults({ ...defaults, paymentMethod: e.target.value })}>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`expenses.payment.${m}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("expenses.fields.card")}>
                <input className={c.input} maxLength={4} value={defaults.cardLast4} onChange={(e) => setDefaults({ ...defaults, cardLast4: e.target.value })} />
              </Field>
              {foreign.map((cur) => (
                <Field key={cur} label={t("expenses.import.rateFor", { currency: cur })}>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    aria-label={`rate-${cur}`}
                    className={`${c.input} ${Number(rates[cur]) > 0 ? "" : "border-amber-500"}`}
                    value={rates[cur] || ""}
                    onChange={(e) => setRates({ ...rates, [cur]: e.target.value })}
                    placeholder={t("expenses.form.ratePlaceholder")}
                  />
                </Field>
              ))}
            </div>
          )}

          <div className={`${c.card} overflow-x-auto p-0`}>
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className={`text-left text-xs ${c.muted}`}>
                  <th className="px-3 py-2" />
                  <th className="px-2 py-2">{t("expenses.fields.date")}</th>
                  <th className="px-2 py-2">{t("expenses.fields.vendor")}</th>
                  <th className="px-2 py-2">{t("expenses.fields.description")}</th>
                  <th className="px-2 py-2">{tt("import.kind")}</th>
                  <th className="px-2 py-2">{t("expenses.fields.category")}</th>
                  <th className="px-2 py-2">{t("expenses.fields.platform")}</th>
                  <th className="px-2 py-2 text-right">{t("expenses.fields.amount")}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const kind = kindOf(r);
                  return (
                    <tr key={r.index} className={`border-t ${c.border} ${c.text} ${r.selected ? "" : "opacity-50"}`} data-testid={`charge-${r.index}`}>
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`select-${r.index}`}
                          className="h-4 w-4 accent-teal-600"
                          disabled={confirmed || !selectable(r)}
                          checked={r.selected}
                          onChange={(e) => patch(i, { selected: e.target.checked, forceDuplicate: e.target.checked && Boolean(r.duplicateOf) })}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="date" className={`${c.inputBase} w-36`} disabled={confirmed} value={r.date || ""} onChange={(e) => patch(i, { date: e.target.value || null })} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className={c.input} disabled={confirmed} value={r.vendor} onChange={(e) => patch(i, { vendor: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className={c.input} disabled={confirmed} value={r.description} onChange={(e) => patch(i, { description: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5">
                        <select aria-label={`kind-${r.index}`} className={c.input} disabled={confirmed} value={kind || ""} onChange={(e) => setKind(i, e.target.value as MovementKind)} title={r.kindReason || undefined}>
                          {!kind && <option value="">—</option>}
                          {MOVEMENT_KINDS.map((k) => (
                            <option key={k} value={k}>
                              {tt(`import.kinds.${k}`)}
                            </option>
                          ))}
                        </select>
                        {kind === "devolucion" && !confirmed && (
                          <select
                            aria-label={`reverts-${r.index}`}
                            className={`${c.input} mt-1`}
                            value={r.revertsIndex != null ? `#${r.revertsIndex}` : r.revertsExpenseId || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              patch(i, v.startsWith("#") ? { revertsIndex: Number(v.slice(1)), revertsExpenseId: null } : { revertsExpenseId: v || null, revertsIndex: null });
                            }}
                          >
                            <option value="">
                              {tt("import.reverts")}: {tt("import.revertsNone")}
                            </option>
                            {r.revertsExpenseId && <option value={r.revertsExpenseId}>{tt("import.reverts")}: {r.vendor}</option>}
                            {sameDocCharges.map((g) => (
                              <option key={g.index} value={`#${g.index}`}>
                                {tt("import.revertsRow", { n: g.index + 1, vendor: g.vendor })}
                              </option>
                            ))}
                          </select>
                        )}
                        {r.kindReason && kind !== "gasto" && <p className={`mt-0.5 max-w-[14rem] text-[11px] leading-tight ${c.muted}`}>{r.kindReason}</p>}
                      </td>
                      <td className="px-2 py-1.5">
                        <select className={c.input} disabled={confirmed} value={r.category} onChange={(e) => patch(i, { category: e.target.value as Row["category"] })}>
                          {categoryOptions(i18n.language).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select className={c.input} disabled={confirmed} value={r.platformId || ""} onChange={(e) => patch(i, { platformId: e.target.value || null })}>
                          <option value="">—</option>
                          {platforms.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
                        {r.isCredit ? "−" : ""}
                        {money(Number(r.amount), r.currency, i18n.language)}
                        {r.currency !== "MXN" && Number(r.exchangeRate) > 0 && (
                          <p className={`text-xs ${c.muted}`}>
                            {money(r.billedAmount ?? Number(r.amount) * Number(r.exchangeRate), "MXN", i18n.language)} · TC {r.exchangeRate}
                          </p>
                        )}
                      </td>
                      <td className="space-x-1 whitespace-nowrap px-3 py-1.5">
                        {r.isCredit && <Chip className="bg-sky-500/15 text-sky-600">{t("expenses.import.credit")}</Chip>}
                        {kind && !kindCreatesExpense(kind) && <Chip className="bg-gray-400/15 text-gray-500">{tt("import.notExpense")}</Chip>}
                        {r.duplicateOf && <Chip className="bg-amber-500/15 text-amber-600">{t("expenses.import.duplicate")}</Chip>}
                        {r.confidence === "baja" && <Chip className="bg-red-500/15 text-red-600">{t("expenses.import.lowConfidence")}</Chip>}
                        {!r.date && <Chip className="bg-red-500/15 text-red-600">{t("expenses.import.noDate")}</Chip>}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className={`px-4 py-6 text-center text-sm ${c.muted}`}>
                      {t("expenses.import.noCharges")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2">
            {busy === "saving" && <Spinner label={t("expenses.saving")} />}
            <button
              type="button"
              className={c.secondary}
              onClick={async () => {
                // Descartar una importación pendiente la borra (y su archivo); una confirmada solo se cierra.
                if (!confirmed) await discardImport(record.id).catch(() => undefined);
                setRecord(null);
                setRows([]);
                void loadHistory();
              }}
            >
              {confirmed ? t("expenses.close") : t("expenses.import.discard")}
            </button>
            {!confirmed && (
              <button
                type="button"
                className={c.primary}
                disabled={!selected.length || selected.some((r) => !r.date) || missingRate || Boolean(busy) || Boolean(clash.exact)}
                onClick={confirm}
              >
                {t("expenses.import.confirm", { count: selected.length })}
              </button>
            )}
          </div>
        </>
      )}

      <div className={c.card}>
        <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.import.history")}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className={`border-t ${c.border} ${c.text}`}>
                  <td className="py-1.5 pr-2 text-xs tabular-nums">{dateTime(h.createdAt, i18n.language)}</td>
                  <td className="py-1.5 pr-2">{h.file?.name}</td>
                  <td className={`py-1.5 pr-2 text-xs ${c.muted}`}>
                    {t(`expenses.import.docType.${h.documentType}`)}
                    {h.format ? ` · ${tt(`import.format.${h.format}`)}` : ""}
                  </td>
                  <td className={`py-1.5 pr-2 text-xs ${c.muted}`}>
                    {h.accountId ? accountLabel(accountById(h.accountId)) : ""}
                    {h.statementPeriod?.from ? ` · ${h.statementPeriod.from} → ${h.statementPeriod.to || "?"}` : ""}
                  </td>
                  <td className="py-1.5 pr-2 text-xs">{t("expenses.import.chargeCount", { count: h.chargeCount ?? 0 })}</td>
                  <td className="space-x-1 whitespace-nowrap py-1.5 pr-2">
                    <Chip className={h.status === "confirmada" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}>
                      {t(`expenses.import.status.${h.status}`)}
                    </Chip>
                    {h.reconciliation && h.documentType === "estado_de_cuenta" && (
                      <Chip className={RECONCILE_TONES[h.reconciliation.status]}>{tt(`reconcile.status.${h.reconciliation.status}`)}</Chip>
                    )}
                  </td>
                  <td className="py-1.5 text-right">
                    <button type="button" className="text-teal-600" onClick={() => open(h.id)}>
                      {h.status === "confirmada" ? t("expenses.import.view") : t("expenses.import.resume")}
                    </button>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td className={`py-3 text-center text-xs ${c.muted}`}>{t("expenses.import.noHistory")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}
